import type { FunctionComponent } from 'react';
import React, {
  Suspense,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import cx from 'classnames';

import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { DynamicButton } from '@trello/dynamic-tokens';
import { forTemplate } from '@trello/i18n';
import type { ButtonProps } from '@trello/nachos/button';
import { Button } from '@trello/nachos/button';
import { PowerUpIcon } from '@trello/nachos/icons/power-up';
import { WarningIcon } from '@trello/nachos/icons/warning';
import { Popover, usePopover } from '@trello/nachos/popover';
import { Spinner } from '@trello/nachos/spinner';
import { Tooltip } from '@trello/nachos/tooltip';
import { useLazyComponent } from '@trello/use-lazy-component';

import { ResponsiveBoardHeaderContext } from 'app/src/components/BoardHeader/ResponsiveBoardHeaderContext';
import { mergeRefs } from 'app/src/mergeRefs';
import type { Board } from './PluginHeaderButton.types';
import { useHasDeprecatedPlugins } from './useHasDeprecatedPlugins';

import styles from './PluginListButton.less';

const format = forTemplate('board_header_pup_dropdown');

interface PluginListButtonProps {
  openDirectory: () => void;
  openPluginSettings: (pluginId: string) => void;
  board: Board;
}

export const PluginListButton: FunctionComponent<PluginListButtonProps> = ({
  openDirectory,
  openPluginSettings,
  board,
}: PluginListButtonProps) => {
  const PluginListPopover = useLazyComponent(
    () =>
      import(
        /* webpackChunkName: "plugin-list-popover" */ 'app/src/components/PluginHeaderButton'
      ),
    {
      namedImport: 'PluginListPopover',
    },
  );

  const numPlugins = board?.powerUpsCount();
  const idBoard = board.id;
  const idOrganization = board?.attributes?.idOrganization;

  const hasDeprecatedPlugins = useHasDeprecatedPlugins(board);

  const [traceId, setTraceId] = useState('');

  const openDirectoryCallback = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'pluginListAddPowerUpsButton',
      source: 'powerUpHubPopover',
      containers: formatContainers({
        idBoard,
        idOrganization,
      }),
    });
    openDirectory();
  }, [openDirectory, idOrganization, idBoard]);

  const startTrace = useCallback(() => {
    setTraceId(
      Analytics.startTask({
        taskName: 'view-power-up-hub',
        source: 'powerUpHubPopover',
      }),
    );
  }, []);

  const { triggerRef, toggle, popoverProps } = usePopover<HTMLButtonElement>({
    onShow: startTrace,
  });

  const buttonRef = useRef<HTMLButtonElement>(null);

  const mergedRef = mergeRefs(triggerRef, buttonRef);

  const openPluginList = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'pluginListButton',
      source: 'boardScreen',
      containers: formatContainers({
        idBoard,
        idOrganization,
      }),
    });
    Analytics.sendScreenEvent({
      name: 'powerUpsMenuInlineDialog',
    });
    toggle();
  }, [toggle, idBoard, idOrganization]);

  let deprecatedPUpsWarningIcon: ButtonProps['iconAfter'];

  if (hasDeprecatedPlugins) {
    deprecatedPUpsWarningIcon = (
      <WarningIcon dangerous_className={styles.deprecatedPluginsWarningIcon} />
    );
  }

  const { isComponentCollapsed } = useContext(ResponsiveBoardHeaderContext);
  const isCollapsed = isComponentCollapsed('PowerupsDirectoryButton');

  return (
    <div>
      <Tooltip content={format('power-ups')}>
        <DynamicButton
          className={cx(
            styles.pluginDropdownButton,
            isCollapsed && styles.hideText,
          )}
          ref={mergedRef}
          iconBefore={
            <PowerUpIcon dangerous_className={styles.icon} size="small" />
          }
          iconAfter={deprecatedPUpsWarningIcon}
          onClick={openPluginList}
          aria-label={format('power-ups')}
        >
          <span className={styles.buttonText}>{format('power-ups')}</span>
        </DynamicButton>
      </Tooltip>
      <Popover
        dangerous_className={styles.removePopoverPadding}
        title={format('power-ups')}
        {...popoverProps}
      >
        <Suspense fallback={<Spinner centered />}>
          {buttonRef.current && (
            <PluginListPopover
              board={board}
              container={buttonRef.current}
              openPluginSettings={openPluginSettings}
              toggle={toggle}
              traceId={traceId}
            />
          )}
          {board.editable() ? (
            <div
              className={cx(
                styles.footerBox,
                numPlugins === 0 && styles.noPlugins,
              )}
            >
              <Button
                appearance={'primary'}
                shouldFitContainer={true}
                onClick={openDirectoryCallback}
              >
                {format('add-power-ups')}
              </Button>
            </div>
          ) : null}
        </Suspense>
      </Popover>
    </div>
  );
};
